import { useAuth } from "@mindlercare/auth-ui";
import { RouterOutput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { countries } from "@mindlercare/schema";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMindlerApi } from "../../api/apiv2";
import { BookingCell } from "./patientCard/PatientBookingCell";
import { sortByBookingDate } from "./utils";
import type { Booking, SearchTerms } from "./types";

type Patient = RouterOutput["patients"]["getAllPatients"][number];

export const usePatients = ({
  searchTerms,
  selectedPsychologistId,
  shouldFetch = true,
}: {
  searchTerms: SearchTerms;
  selectedPsychologistId: number | undefined;
  shouldFetch?: boolean;
}) => {
  const { market } = useAuth();
  const [patients, setPatients] = useState<Patient[]>([]);
  const mutationOptsRef = useRef<string>();
  const api = useMindlerApi();
  const { mutate, isPending } = useMutation(
    api.patients.getAllPatients.mutationOptions({
      onSuccess(data) {
        setPatients(data);
      },
    }),
  );
  useEffect(() => {
    if (!shouldFetch) return;
    const mutationOpts = {
      searchTerms,
      // @TODO: God, why do we still use psychologistId in new procedures?..
      selectedPsychologistId:
        selectedPsychologistId === -1 ? undefined : selectedPsychologistId,
      countryId: countries[market].countryId,
      limit: 100,
    };
    if (mutationOptsRef.current === JSON.stringify(mutationOpts)) return;
    mutationOptsRef.current = JSON.stringify(mutationOpts);
    mutate(mutationOpts);
  }, [market, mutate, searchTerms, selectedPsychologistId, shouldFetch]);

  const formatPatientTableData = (data: Patient[]) =>
    data
      .filter((p) => ![p.firstName, p.lastName].includes("[DELETED]"))
      .map((patient) => {
        const {
          firstName,
          lastName,
          nationalIdentity,
          previousBooking,
          nextBooking,
          userId,
          treatmentStatusType,
        } = patient;

        const { patientBookingStatus } = patient as Patient & {
          patientBookingStatus?: string;
        };

        return {
          firstName,
          lastName,
          // we want this not to be empty string, so we can show ***** for this field
          // or we just remove this field for other markets than SE
          nationalIdentity: nationalIdentity ?? " ",
          userId,
          id: `${userId}`,
          previousBooking,
          nextBooking,
          treatmentStatusType,
          patientBookingStatus,
        };
      });

  const data = formatPatientTableData(patients ?? []);
  const columns = useMemo<ColumnDef<(typeof data)[number]>[]>(
    () => [
      {
        id: "name",
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: "NAME",
        meta: {
          sensitive: true,
        },
        size: 260,
        minSize: 260,
        maxSize: 260,
      },
      {
        accessorKey: "nationalIdentity",
        header: "NATIONAL_IDENTITY",
        meta: {
          sensitive: true,
        },
        size: 180,
        minSize: 180,
        maxSize: 180,
      },
      {
        accessorKey: "userId",
        header: "USER_ID",
        meta: {
          sensitive: true,
        },
        size: 120,
        minSize: 120,
        maxSize: 120,
      },
      {
        accessorKey: "previousBooking",
        header: "PREVIOUS_BOOKING",
        meta: { sensitive: true },
        size: 200,
        minSize: 200,
        maxSize: 200,
        cell: ({ getValue }) => <BookingCell booking={getValue() as Booking} />,
        sortingFn: sortByBookingDate("previousBooking"),
      },
      {
        accessorKey: "nextBooking",
        header: "NEXT_BOOKING",
        meta: { sensitive: true },
        size: 200,
        minSize: 200,
        maxSize: 200,
        cell: ({ getValue }) => <BookingCell booking={getValue() as Booking} />,
        sortingFn: sortByBookingDate("nextBooking"),
      },
    ],
    [],
  );

  const columnsVisibility = useMemo(
    () => ({
      nationalIdentity: market === "SE",
    }),
    [market],
  );

  return {
    data,
    columns,
    columnsVisibility,
    isPending,
  };
};
